import { IonIcon, IonToast } from '@ionic/react';
import classNames from 'classnames';
import { close } from 'ionicons/icons';
import * as React from 'react';

import type { FilterableBadgeCategory } from '@biteinc/enums';
import { BadgeCategory, BadgeCategoryHelper } from '@biteinc/enums';
import { Strings } from '@biteinc/localization';

import { useLocalize } from '~/app/js/localization/localization';
import { MenuItemUtils } from '~/helpers';
import type { KioskMenu } from '~/types';

import { useMenu, useStore } from '../stores';
import { ReactAriaButton } from './button';

type MenuFilterPickerProps = {
  close: () => void;
};

export function MenuFilterPicker(props: MenuFilterPickerProps): JSX.Element {
  const str = useLocalize();
  const menu = useMenu();
  const menuFilters = useStore((state) => state.config.menuFilters);
  const clearMenuFilters = useStore((state) => state.config.clearMenuFilters);
  const removeMenuFilter = useStore((state) => state.config.removeMenuFilter);
  const addMenuFilter = useStore((state) => state.config.addMenuFilter);

  const badges = menu.badges.reduce<Record<FilterableBadgeCategory, KioskMenu['badges'][number][]>>(
    (acc, badge) => {
      if (!BadgeCategoryHelper.isFilterableCategory(badge.filterCategory)) {
        return acc;
      }

      if (badge.filterCategory) {
        acc[badge.filterCategory].push(badge);
      }

      return acc;
    },
    {
      [BadgeCategory.Dietary]: [],
      [BadgeCategory.Restrictive]: [],
      [BadgeCategory.Other]: [],
    },
  );

  function toggleFilter(filter: KioskMenu['badges'][number]): void {
    if (BadgeCategoryHelper.isFilterableCategory(filter.filterCategory)) {
      const data = {
        category: filter.filterCategory,
        id: filter._id,
      };
      if (menuFilters[data.category].includes(filter._id)) {
        removeMenuFilter(data);
      } else {
        addMenuFilter(data);
      }
    }
  }

  function shouldShowNoMatchingItems(): boolean {
    return (
      Object.values(menuFilters).some((ids) => ids.length > 0) &&
      !menu.items.some((item) => MenuItemUtils.matchesBadgesInSet(item, menuFilters))
    );
  }

  const showNoMatchingItems = shouldShowNoMatchingItems();
  return (
    <div className="menu-filter-picker tw-flex tw-flex-1 tw-flex-col tw-text-white">
      <div className="max-w-[700px] tw-mx-auto tw-flex tw-w-full tw-flex-1 tw-flex-col tw-space-y-4 tw-p-4">
        <div className="tw-flex tw-items-center tw-justify-center tw-space-x-10">
          <h2 className="menu-filter-picker-header tw-text-2xl tw-font-bold tw-uppercase">
            {str(Strings.MENU_FILTERS_NUTRITION_PREFERENCE)}
          </h2>
          <ReactAriaButton
            className="menu-filter-picker-close-button tw-rounded-full tw-p-8 tw-text-2xl"
            onPress={() => {
              props.close();
            }}
          >
            <IonIcon
              slot="icon-only"
              icon={close}
            />
          </ReactAriaButton>
        </div>
        {badges.restrictive.length > 0 && (
          <div className="menu-filter-picker-section tw-flex tw-flex-col tw-space-y-1">
            <h6 className="menu-filter-picker-section-title tw-text-lg">
              {str(Strings.MENU_FILTERS_RESTRICTIVE_FILTERS)}
            </h6>
            <p className="menu-filter-picker-section-description tw-text-sm">
              {str(Strings.MENU_FILTERS_RESTRICTIVE_FILTERS_DESCRIPTION)}
            </p>
            <div className="tw-mt-2 tw-flex tw-items-center tw-space-x-4 tw-overflow-x-auto">
              {badges.restrictive.map((filter) => (
                <FilterButton
                  key={filter._id}
                  filter={filter}
                  isSelected={menuFilters.restrictive.includes(filter._id)}
                  onPress={() => toggleFilter(filter)}
                />
              ))}
            </div>
          </div>
        )}
        {badges.dietary.length > 0 && (
          <div className="menu-filter-picker-section tw-flex tw-flex-col tw-space-y-1">
            <h6 className="menu-filter-picker-section-title tw-text-lg">
              {str(Strings.MENU_FILTERS_DIETARY_FILTERS)}
            </h6>
            <p className="menu-filter-picker-section-description tw-text-sm">
              {str(Strings.MENU_FILTERS_DIETARY_FILTERS_DESCRIPTION)}
            </p>
            <div className="tw-mt-2 tw-flex tw-items-center tw-space-x-4">
              {badges.dietary.map((filter) => (
                <FilterButton
                  key={filter._id}
                  filter={filter}
                  isSelected={menuFilters.dietary.includes(filter._id)}
                  onPress={() => toggleFilter(filter)}
                />
              ))}
            </div>
          </div>
        )}
        {badges.other.length > 0 && (
          <div className="menu-filter-picker-section tw-flex tw-flex-col tw-space-y-1">
            <h6 className="menu-filter-picker-section-title tw-text-lg">
              {str(Strings.MENU_FILTERS_OTHER_FILTERS)}
            </h6>
            <p className="menu-filter-picker-section-description tw-text-sm">
              {str(Strings.MENU_FILTERS_OTHER_FILTERS_DESCRIPTION)}
            </p>
            <div className="tw-mt-2 tw-flex tw-items-center tw-space-x-4">
              {badges.other.map((filter) => (
                <FilterButton
                  key={filter._id}
                  filter={filter}
                  isSelected={menuFilters.other.includes(filter._id)}
                  onPress={() => toggleFilter(filter)}
                />
              ))}
            </div>
          </div>
        )}
        <IonToast
          isOpen={showNoMatchingItems}
          color="danger"
          message={str(Strings.NO_ITEMS_MATCH_FILTERS).toString()}
          cssClass="modal-toast"
        />
      </div>
      <div className="menu-filter-picker-footer tw-flex tw-items-center tw-justify-center tw-space-x-4 tw-bg-[#333] tw-p-4">
        <ReactAriaButton
          style={{
            //@ts-expect-error
            '--bg-color': '#333',
            '--text-color': 'rgb(200,199,204)',
          }}
          className="menu-filter-picker-footer-button tw-flex tw-min-w-[275px] tw-items-center tw-justify-center tw-rounded-md tw-border tw-border-solid tw-border-[--text-color] tw-bg-[--bg-color] tw-px-16 tw-py-2 tw-font-bold tw-text-[--text-color] hover:tw-opacity-80 data-[pressed]:tw-opacity-80"
          onPress={() => {
            clearMenuFilters();
          }}
        >
          {str(Strings.CLEAR_ALL)}
        </ReactAriaButton>
        <ReactAriaButton
          style={{
            //@ts-expect-error
            '--bg-color': 'rgb(200,199,204)',
            '--text-color': '#333',
          }}
          className="menu-filter-picker-footer-button tw-flex tw-min-w-[275px] tw-items-center tw-justify-center tw-rounded-md tw-border tw-border-solid tw-border-[--text-color] tw-bg-[--bg-color] tw-px-16 tw-py-2 tw-font-bold tw-text-[--text-color] hover:tw-opacity-80 data-[pressed]:tw-opacity-80"
          onPress={() => {
            props.close();
          }}
          disabled={showNoMatchingItems}
        >
          {str(Strings.BACK_TO_MENU)}
        </ReactAriaButton>
      </div>
    </div>
  );
}

function FilterButton(props: {
  isSelected: boolean;
  onPress: () => void;
  filter: KioskMenu['badges'][number];
}): JSX.Element {
  const str = useLocalize();
  const { onPress, isSelected } = props;
  const label = props.filter.name;
  const icon = props.filter.icons[0].url;

  return (
    <ReactAriaButton
      className={classNames(
        `menu-filter-picker-filter-button tw-flex tw-min-w-[180px] tw-items-center tw-space-x-2 tw-rounded-lg tw-p-2 tw-pr-6 tw-font-[--font-title]`,
        {
          'tw-text-black [--bg-opacity:1] hover:[--bg-opacity:0.9] data-[pressed]:[--bg-opacity:0.9]':
            isSelected,
          '[--bg-opacity:0.3] hover:[--bg-opacity:0.8] data-[pressed]:[--bg-opacity:0.8]':
            !isSelected,
        },
      )}
      style={{
        backgroundColor: `rgba(255,255,255,var(--bg-opacity))`,
      }}
      data-selected={isSelected ? '' : undefined}
      onPress={onPress}
      aria-label={str(label).toString()}
    >
      <span
        className="menu-filter-picker-filter-button-icon tw-h-9 tw-w-9 tw-rounded-full tw-border-2 tw-border-solid tw-border-white tw-bg-[--card-background] tw-bg-cover tw-bg-center tw-bg-no-repeat tw-p-1.5"
        style={{ backgroundImage: `url(${icon})` }}
      />
      <span className="menu-filter-picker-filter-button-label tw-font-bold">{str(label)}</span>
    </ReactAriaButton>
  );
}
